import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { FaClock, FaComments, FaBoxOpen } from 'react-icons/fa';

export default function MyProposals() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchQuotes();
    }
  }, [user]);

  const fetchQuotes = async () => {
    try {
      const res = await axios.get(`${API}/quotes/client/my-quotes?user_id=${user.id}`);
      setQuotes(res.data);
    } catch (e) {
      console.error('Error loading client quotes', e);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    if (i18n.language === 'fr') {
      if (status === 'quoted') return 'Proposition reçue';
      if (status === 'contacted') return 'En discussion';
      if (status === 'closed') return 'Clôturé';
      if (status === 'accepted') return 'Validé';
      if (status === 'shipped') return 'Expédié';
      return 'En attente';
    }
    if (status === 'quoted') return 'Proposal received';
    if (status === 'contacted') return 'In discussion';
    if (status === 'closed') return 'Closed';
    if (status === 'accepted') return 'Accepted';
    if (status === 'shipped') return 'Shipped';
    return 'Pending';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <p className="text-gray-600">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0A2540] mb-2">
            {i18n.language === 'fr' ? 'Mes Propositions' : 'My Proposals'}
          </h1>
          <p className="text-gray-600">
            {i18n.language === 'fr'
              ? 'Suivez vos demandes de devis et les propositions reçues'
              : 'Track your quote requests and received proposals'}
          </p>
        </div>

        {quotes.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-gray-600 mb-2">
                {i18n.language === 'fr'
                  ? 'Vous n\'avez pas encore de demandes de devis liées à votre compte.'
                  : 'You do not have any quote requests linked to your account yet.'}
              </p>
              <p className="text-gray-500 text-sm">
                {i18n.language === 'fr'
                  ? 'Faites une demande de devis depuis une fiche produit pour voir les réponses ici.'
                  : 'Send a quote request from a product page to see replies here.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {quotes.map((q) => (
              <Card key={q.id} className="hover:shadow-md transition">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div>
                      <h2 className="text-xl font-semibold text-[#0A2540] mb-1">
                        {q.product_name}
                      </h2>
                      <p className="text-sm text-gray-600 mb-2">
                        <FaBoxOpen className="inline mr-2" />
                        {i18n.language === 'fr' ? 'Type:' : 'Type:'}{' '}
                        {q.product_type === 'new'
                          ? i18n.language === 'fr' ? 'Neuf' : 'New'
                          : q.product_type === 'used'
                          ? i18n.language === 'fr' ? 'Occasion' : 'Used'
                          : i18n.language === 'fr' ? 'Location' : 'Rental'}
                      </p>
                      {q.message && (
                        <p className="text-sm text-gray-500 line-clamp-2">
                          {q.message}
                        </p>
                      )}
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
                        {getStatusLabel(q.status)}
                      </span>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <FaClock />
                        <span>
                          {q.created_at
                            ? new Date(q.created_at).toLocaleString(
                                i18n.language === 'fr' ? 'fr-FR' : 'en-US'
                              )
                            : ''}
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => navigate(`/user/proposals/${q.id}`)}
                      >
                        <FaComments className="mr-2" />
                        {i18n.language === 'fr' ? 'Ouvrir la discussion' : 'Open thread'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
